import { supabase } from '../supabaseClient';
import {
  buildInProgressDailyAttempt,
  computeDailyStatsFromAttempts,
  normalizeDailyAttempt,
} from './dailyGameAttemptUtils';

const DAILY_ATTEMPTS_TABLE = 'daily_game_attempts';

const mapDbRowToAttempt = (row) =>
  normalizeDailyAttempt({
    guesses: row?.guesses,
    currentGuess: row?.current_guess,
    currentRow: row?.current_row,
    gameOver: row?.game_over,
    isWin: row?.is_win,
    guessesUsed: row?.guesses_used,
  });

const mapAttemptToDbPayload = (attempt) => {
  const normalized = normalizeDailyAttempt(attempt);
  return {
    guesses: normalized.guesses,
    current_guess: normalized.currentGuess,
    current_row: normalized.currentRow,
    game_over: normalized.gameOver,
    is_win: normalized.isWin,
    guesses_used: normalized.guessesUsed,
    completed_at: normalized.gameOver ? new Date().toISOString() : null,
    updated_at: new Date().toISOString(),
  };
};

export const getDailyAttemptDb = async ({ userId, puzzleDateUtc }) => {
  if (!userId || !puzzleDateUtc) return null;
  try {
    const { data, error } = await supabase
      .from(DAILY_ATTEMPTS_TABLE)
      .select(
        'guesses, current_guess, current_row, game_over, is_win, guesses_used',
      )
      .eq('user_id', userId)
      .eq('puzzle_date', puzzleDateUtc)
      .maybeSingle();

    if (error) {
      console.error('[daily] Failed to read signed-in attempt:', error);
      return null;
    }

    if (!data) return null;
    return mapDbRowToAttempt(data);
  } catch (error) {
    console.error('[daily] Unexpected error reading signed-in attempt:', error);
    return null;
  }
};

export const ensureDailyAttemptDb = async ({
  userId,
  puzzleDateUtc,
  gameState,
}) => {
  if (!userId || !puzzleDateUtc) return null;

  const existing = await getDailyAttemptDb({ userId, puzzleDateUtc });
  if (existing) return existing;

  const freshAttempt = buildInProgressDailyAttempt(gameState);

  try {
    const { error } = await supabase.from(DAILY_ATTEMPTS_TABLE).insert({
      user_id: userId,
      puzzle_date: puzzleDateUtc,
      ...mapAttemptToDbPayload(freshAttempt),
    });

    if (error && error.code !== '23505') {
      console.error('[daily] Failed to create signed-in attempt:', error);
      return freshAttempt;
    }
  } catch (error) {
    console.error('[daily] Unexpected error creating signed-in attempt:', error);
    return freshAttempt;
  }

  const created = await getDailyAttemptDb({ userId, puzzleDateUtc });
  return created || freshAttempt;
};

export const saveDailyAttemptDb = async ({
  userId,
  puzzleDateUtc,
  attempt,
}) => {
  if (!userId || !puzzleDateUtc) return false;
  try {
    const payload = mapAttemptToDbPayload(attempt);
    const { data, error } = await supabase
      .from(DAILY_ATTEMPTS_TABLE)
      .update(payload)
      .eq('user_id', userId)
      .eq('puzzle_date', puzzleDateUtc)
      .select('user_id')
      .maybeSingle();

    if (error) {
      console.error('[daily] Failed to save signed-in attempt:', error);
      return false;
    }

    if (!data) {
      // Row is missing or update was blocked by RLS/immutability (e.g. completed elsewhere).
      return false;
    }

    return true;
  } catch (error) {
    console.error('[daily] Unexpected error saving signed-in attempt:', error);
    return false;
  }
};

export const getDailyStatsDb = async ({ userId }) => {
  if (!userId) return computeDailyStatsFromAttempts([]);

  try {
    const { data, error } = await supabase
      .from(DAILY_ATTEMPTS_TABLE)
      .select('puzzle_date, game_over, is_win, guesses_used')
      .eq('user_id', userId)
      .order('puzzle_date', { ascending: true });

    if (error) {
      console.error('[daily] Failed to load signed-in daily stats:', error);
      return computeDailyStatsFromAttempts([]);
    }

    return computeDailyStatsFromAttempts(data || []);
  } catch (error) {
    console.error('[daily] Unexpected error loading signed-in daily stats:', error);
    return computeDailyStatsFromAttempts([]);
  }
};
